import React from 'react';
import { X, Shield, Database, Eye, Lock, Users, Globe, Mail, AlertCircle, Baby, Building2, Brain, Smartphone } from 'lucide-react';

interface PrivacyPolicyModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function PrivacyPolicyModal({ isOpen, onClose }: PrivacyPolicyModalProps) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-xl max-w-5xl w-full max-h-[95vh] overflow-hidden my-4 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-white shrink-0">
          <div className="flex items-center space-x-3">
            <Shield className="w-6 h-6 text-[#1F2937]" />
            <h2 className="text-xl font-bold text-gray-900">Informativa sulla Privacy (Privacy Policy)</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Chiudi"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto flex-1 min-h-0">
          <div className="prose prose-gray max-w-none">
            {/* Intro Box */}
            <div className="bg-[#1F2937]/10 border border-[#1F2937]/20 rounded-lg p-4 mb-6">
              <div className="flex items-center space-x-2 mb-2">
                <Shield className="w-5 h-5 text-[#1F2937]" />
                <span className="font-semibold text-[#1F2937]">I tuoi dati sono protetti</span>
              </div>
              <p className="text-gray-700 text-sm mb-2">
                Questa Privacy Policy (Informativa sulla Privacy) spiega in modo trasparente come Obly Srls raccoglie,
                utilizza, condivide e protegge le tue informazioni personali in conformità a:
              </p>
              <ul className="text-gray-700 text-sm space-y-1 ml-4">
                <li>• <strong>Regolamento UE 2016/679</strong> - GDPR (Regolamento Generale sulla Protezione dei Dati)</li>
                <li>• <strong>D.Lgs. 196/2003</strong> come modificato dal <strong>D.Lgs. 101/2018</strong> (Codice Privacy italiano)</li>
                <li>• <strong>Linee guida e provvedimenti del Garante Privacy italiano</strong></li>
              </ul>
            </div>

            {/* 1. TITOLARE DEL TRATTAMENTO */}
            <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center space-x-2">
              <Building2 className="w-5 h-5 text-[#1F2937]" />
              <span>1. Titolare del Trattamento dei Dati Personali</span>
            </h3>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
              <p className="text-gray-700 text-sm mb-3">
                Il Titolare del trattamento dei dati personali raccolti tramite la piattaforma Flusia è:
              </p>
              <div className="bg-white border border-gray-200 rounded p-3">
                <p className="text-gray-700 text-sm">
                  <strong>Obly Srls</strong><br />
                  Sede Legale: Piazza Martiri della Libertà 2, 15033 Casale Monferrato (AL), Italia<br />
                  Partita IVA: 02730180060<br />
                  Email: <a href="mailto:digital@obly.it" className="text-[#1F2937] hover:underline">digital@obly.it</a><br />
                  PEC: (se disponibile)<br />
                  Sito Web: <a href="https://flusia.it" className="text-[#1F2937] hover:underline" target="_blank" rel="noopener noreferrer">flusia.it</a>
                </p>
              </div>
              <p className="text-gray-700 text-sm mt-3">
                <strong>Data Protection Officer (DPO):</strong> Al momento non è stato nominato un DPO in quanto non ricorrono
                le condizioni di cui all'Art. 37 GDPR (trattamenti su larga scala di categorie particolari di dati o monitoraggio sistematico).
                Il Titolare può comunque procedere alla nomina volontaria. Per questioni privacy, contattare: digital@obly.it
              </p>
            </div>

            {/* 2. CATEGORIE DI DATI RACCOLTI */}
            <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center space-x-2">
              <Database className="w-5 h-5 text-[#1F2937]" />
              <span>2. Categorie di Dati Personali che Raccogliamo</span>
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              {/* Dati di Registrazione */}
              <div className="border border-[#1F2937]/20 rounded-lg p-4 bg-[#1F2937]/5">
                <h4 className="font-semibold text-gray-900 text-sm mb-2 flex items-center space-x-2">
                  <Users className="w-4 h-4 text-[#1F2937]" />
                  <span>A) Dati di Registrazione e Contatto</span>
                </h4>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>• Nome e cognome</li>
                  <li>• Indirizzo email (aziendale o personale)</li>
                  <li>• Numero di telefono</li>
                  <li>• Ragione sociale / Nome azienda</li>
                  <li>• Partita IVA / Codice Fiscale (per fatturazione)</li>
                  <li>• Indirizzo sede legale / operativa</li>
                  <li>• Ruolo aziendale (es. CEO, Sales Manager)</li>
                </ul>
              </div>

              {/* Dati di Utilizzo */}
              <div className="border border-[#1F2937]/20 rounded-lg p-4 bg-[#1F2937]/5">
                <h4 className="font-semibold text-gray-900 text-sm mb-2 flex items-center space-x-2">
                  <Eye className="w-4 h-4 text-[#1F2937]" />
                  <span>B) Dati di Utilizzo e Navigazione</span>
                </h4>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>• Log di accesso (data, ora, durata sessione)</li>
                  <li>• Indirizzo IP e geolocalizzazione approssimativa</li>
                  <li>• Browser, dispositivo e sistema operativo</li>
                  <li>• Tipo di connessione internet</li>
                  <li>• Pagine visitate e funzionalità utilizzate</li>
                  <li>• Interazioni con la piattaforma (click, scroll, tempo)</li>
                  <li>• Dati di navigazione e preferenze utente</li>
                </ul>
              </div>

              {/* Dati Business Inseriti */}
              <div className="border border-[#1F2937]/20 rounded-lg p-4 bg-[#1F2937]/5">
                <h4 className="font-semibold text-gray-900 text-sm mb-2 flex items-center space-x-2">
                  <Database className="w-4 h-4 text-[#1F2937]" />
                  <span>C) Dati Business Inseriti dal Cliente</span>
                </h4>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>• Dati dei clienti del Cliente (CRM: nomi, contatti, note)</li>
                  <li>• Dati trattative e opportunità commerciali</li>
                  <li>• Preventivi, ordini, fatture generate</li>
                  <li>• Progetti, task e milestone</li>
                  <li>• Email inviate/ricevute tramite piattaforma</li>
                  <li>• Documenti e file caricati</li>
                  <li>• Note, commenti e interazioni interne team</li>
                </ul>
                <p className="text-xs text-gray-600 mt-2 italic">
                  Nota: Questi dati rimangono di proprietà del Cliente. Obly agisce come Responsabile del Trattamento
                  per questi dati, su istruzione del Cliente (Titolare).
                </p>
              </div>

              {/* Dati di Pagamento */}
              <div className="border border-[#1F2937]/20 rounded-lg p-4 bg-[#1F2937]/5">
                <h4 className="font-semibold text-gray-900 text-sm mb-2 flex items-center space-x-2">
                  <Lock className="w-4 h-4 text-[#1F2937]" />
                  <span>D) Dati di Pagamento e Fatturazione</span>
                </h4>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>• Metodo di pagamento selezionato</li>
                  <li>• Ultime 4 cifre carta di credito (se applicabile)</li>
                  <li>• Dati fatturazione (P.IVA, indirizzo fiscale)</li>
                  <li>• Storico transazioni e fatture emesse</li>
                  <li>• Informazioni bancarie per bonifici</li>
                </ul>
                <p className="text-xs text-gray-600 mt-2 italic">
                  Nota: I dati completi di carta di credito sono gestiti direttamente da payment processors certificati
                  PCI-DSS (es. Stripe). Obly non memorizza mai numeri completi di carte di credito.
                </p>
              </div>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-6">
              <p className="text-gray-700 text-sm">
                <strong>Dati Particolari (Art. 9 GDPR):</strong> Flusia <strong>non raccoglie intenzionalmente</strong> dati
                relativi a origine razziale/etnica, opinioni politiche, convinzioni religiose, salute, vita sessuale, dati genetici/biometrici,
                salvo che il Cliente inserisca tali dati nei campi liberi (es. note cliente). In tal caso, il Cliente è responsabile
                della liceità del trattamento e dell'ottenimento dei consensi necessari.
              </p>
            </div>

            {/* 3. FINALITÀ E BASE GIURIDICA */}
            <h3 className="text-lg font-semibold text-gray-900 mb-3">3. Finalità del Trattamento e Base Giuridica (Art. 6 GDPR)</h3>
            <p className="text-gray-700 text-sm mb-4">
              Trattiamo i tuoi dati personali per le seguenti finalità, ciascuna basata su una specifica base giuridica prevista dal GDPR:
            </p>

            <div className="space-y-3 mb-6">
              <div className="border border-[#1F2937]/20 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 text-sm mb-2">
                  <span className="text-[#1F2937]">A) Fornitura del Servizio</span> (Art. 6.1.b GDPR - Esecuzione Contratto)
                </h4>
                <p className="text-gray-700 text-sm mb-2">Per fornire i servizi richiesti tramite la piattaforma Flusia:</p>
                <ul className="text-gray-700 text-sm space-y-1 ml-4">
                  <li>• Creazione e gestione del tuo Account</li>
                  <li>• Erogazione funzionalità CRM, preventivi, progetti, email, WhatsApp</li>
                  <li>• Elaborazione e archiviazione dei dati inseriti</li>
                  <li>• Backup automatici per garantire continuità del servizio</li>
                  <li>• Autenticazione e gestione accessi utenti multipli</li>
                </ul>
              </div>

              <div className="border border-[#1F2937]/20 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 text-sm mb-2">
                  <span className="text-[#1F2937]">B) Comunicazioni di Servizio</span> (Art. 6.1.b GDPR - Esecuzione Contratto)
                </h4>
                <ul className="text-gray-700 text-sm space-y-1 ml-4">
                  <li>• Invio email di conferma registrazione, recupero password</li>
                  <li>• Notifiche su attività importanti (es. nuovi lead, task assegnati)</li>
                  <li>• Aggiornamenti tecnici, manutenzioni programmate, downtime</li>
                  <li>• Supporto clienti e risposta a richieste di assistenza</li>
                  <li>• Fatture, ricevute, scadenze pagamenti</li>
                </ul>
              </div>

              <div className="border border-[#1F2937]/20 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 text-sm mb-2">
                  <span className="text-[#1F2937]">C) Adempimenti Contabili e Fiscali</span> (Art. 6.1.c GDPR - Obbligo Legale)
                </h4>
                <ul className="text-gray-700 text-sm space-y-1 ml-4">
                  <li>• Emissione fatture elettroniche tramite Sistema di Interscambio (SDI)</li>
                  <li>• Conservazione documenti contabili per 10 anni (Art. 2220 c.c., D.P.R. 600/1973)</li>
                  <li>• Adempimenti IVA, dichiarazioni fiscali, comunicazioni Agenzia Entrate</li>
                  <li>• Risposta a richieste di autorità fiscali (Guardia di Finanza)</li>
                </ul>
              </div>

              <div className="border border-[#1F2937]/20 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 text-sm mb-2">
                  <span className="text-[#1F2937]">D) Miglioramento Servizi e Analytics</span> (Art. 6.1.f GDPR - Legittimo Interesse)
                </h4>
                <ul className="text-gray-700 text-sm space-y-1 ml-4">
                  <li>• Analisi statistiche aggregate e anonimizzate sull'utilizzo della piattaforma</li>
                  <li>• Identificazione bug, problemi performance, colli di bottiglia</li>
                  <li>• Sviluppo nuove funzionalità basate su feedback utenti</li>
                  <li>• Ottimizzazione UX/UI, A/B testing su elementi non critici</li>
                  <li>• Prevenzione frodi, spam e abusi della piattaforma</li>
                </ul>
                <p className="text-xs text-gray-600 mt-2 italic">
                  Hai il diritto di opporti a questo trattamento ai sensi dell'Art. 21 GDPR. Contattaci a digital@obly.it
                </p>
              </div>

              <div className="border border-[#1F2937]/20 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 text-sm mb-2">
                  <span className="text-[#1F2937]">E) Marketing e Comunicazioni Promozionali</span> (Art. 6.1.a GDPR - Consenso Esplicito)
                </h4>
                <ul className="text-gray-700 text-sm space-y-1 ml-4">
                  <li>• Invio newsletter con aggiornamenti prodotto, best practices, case studies</li>
                  <li>• Email marketing su nuove funzionalità, promozioni, webinar, eventi</li>
                  <li>• Comunicazioni commerciali via email, WhatsApp, SMS (solo previo consenso opt-in)</li>
                </ul>
                <p className="text-xs text-gray-600 mt-2 italic">
                  <strong>Puoi revocare il consenso in qualsiasi momento</strong> cliccando "Unsubscribe" nelle email o
                  scrivendo a digital@obly.it. La revoca non pregiudica la liceità del trattamento antecedente.
                </p>
              </div>

              <div className="border border-[#1F2937]/20 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 text-sm mb-2">
                  <span className="text-[#1F2937]">F) Difesa Diritti e Tutela Legale</span> (Art. 6.1.f GDPR - Legittimo Interesse)
                </h4>
                <ul className="text-gray-700 text-sm space-y-1 ml-4">
                  <li>• Accertamento, esercizio o difesa di diritti in sede giudiziaria</li>
                  <li>• Risposta a richieste legittime di autorità giudiziarie o forze dell'ordine</li>
                  <li>• Prevenzione e contrasto frodi, violazioni Termini di Servizio</li>
                  <li>• Protezione sicurezza piattaforma e altri utenti</li>
                </ul>
              </div>
            </div>

            {/* 4. DESTINATARI E COMUNICAZIONE DATI */}
            <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center space-x-2">
              <Users className="w-5 h-5 text-[#1F2937]" />
              <span>4. Destinatari e Categorie di Destinatari dei Dati</span>
            </h3>
            <p className="text-gray-700 text-sm mb-3">
              I tuoi dati personali potranno essere comunicati o condivisi con le seguenti categorie di destinatari:
            </p>

            <div className="space-y-3 mb-6">
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                <h4 className="font-semibold text-gray-900 text-sm mb-2">A) Responsabili del Trattamento (Art. 28 GDPR)</h4>
                <p className="text-gray-700 text-sm mb-2">
                  Fornitori di servizi terzi che trattano dati per nostro conto, vincolati da contratti conformi al GDPR:
                </p>
                <ul className="text-gray-700 text-sm space-y-1 ml-4">
                  <li>• <strong>Supabase Inc.</strong> - Hosting database e backend (server UE, USA con clausole standard UE)</li>
                  <li>• <strong>Stripe Inc.</strong> - Elaborazione pagamenti (certificato PCI-DSS, USA con Privacy Shield successore)</li>
                  <li>• <strong>SendGrid (Twilio)</strong> - Invio email transazionali (server UE disponibili)</li>
                  <li>• <strong>WAHA / WhatsApp Business API</strong> - Comunicazioni WhatsApp (conforme Meta Terms)</li>
                  <li>• <strong>Amazon Web Services (AWS)</strong> - Cloud storage backup (regione EU-Central-1 Francoforte)</li>
                  <li>• Provider analytics (Google Analytics 4 con IP anonimizzato, o Plausible Analytics privacy-first)</li>
                </ul>
              </div>

              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                <h4 className="font-semibold text-gray-900 text-sm mb-2">B) Soggetti Autorizzati</h4>
                <ul className="text-gray-700 text-sm space-y-1 ml-4">
                  <li>• Dipendenti e collaboratori di Obly Srls autorizzati al trattamento (principio "need-to-know")</li>
                  <li>• Amministratori di sistema con accesso limitato per manutenzione e sicurezza</li>
                </ul>
              </div>

              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                <h4 className="font-semibold text-gray-900 text-sm mb-2">C) Autorità Pubbliche e Forze dell'Ordine</h4>
                <ul className="text-gray-700 text-sm space-y-1 ml-4">
                  <li>• Agenzia delle Entrate, Guardia di Finanza (per obblighi fiscali e contabili)</li>
                  <li>• Autorità Giudiziaria (in caso di indagini, procedimenti penali, ordini giudiziari)</li>
                  <li>• Polizia Postale (per segnalazioni reati informatici, phishing, frodi)</li>
                  <li>• Garante per la protezione dei dati personali (in caso di ispezioni o reclami)</li>
                </ul>
              </div>

              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                <h4 className="font-semibold text-gray-900 text-sm mb-2">D) Professionisti e Consulenti</h4>
                <ul className="text-gray-700 text-sm space-y-1 ml-4">
                  <li>• Commercialisti, revisori contabili (per adempimenti fiscali)</li>
                  <li>• Avvocati e studi legali (per consulenze e contenziosi)</li>
                  <li>• Consulenti IT e sicurezza informatica (vincolati da NDA)</li>
                </ul>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <p className="text-gray-700 text-sm">
                <strong>Non vendiamo MAI i tuoi dati personali a terzi per scopi commerciali.</strong> Non cediamo database
                di contatti a broker di dati, agenzie marketing non autorizzate, o altre aziende per profitto.
                Condividiamo dati solo quando strettamente necessario per le finalità sopra indicate o richiesto per legge.
              </p>
            </div>

            {/* 5. TRASFERIMENTI DATI PAESI TERZI */}
            <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center space-x-2">
              <Globe className="w-5 h-5 text-[#1F2937]" />
              <span>5. Trasferimento Dati a Paesi Terzi (Extra-UE)</span>
            </h3>
            <p className="text-gray-700 text-sm mb-3">
              Alcuni fornitori di servizi (Responsabili del Trattamento) potrebbero trovarsi al di fuori dello Spazio Economico Europeo (SEE),
              in particolare negli Stati Uniti. Per garantire un livello adeguato di protezione ai sensi degli artt. 44-50 GDPR, ci avvaliamo di:
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
              <div className="border border-[#1F2937]/20 rounded-lg p-3">
                <h4 className="font-semibold text-gray-900 text-sm mb-2">Decisioni di Adeguatezza</h4>
                <p className="text-xs text-gray-600">
                  Art. 45 GDPR: Trasferimenti verso paesi riconosciuti dalla Commissione UE come adeguati (es. Svizzera, UK post-Brexit, Canada per settori specifici)
                </p>
              </div>

              <div className="border border-[#1F2937]/20 rounded-lg p-3">
                <h4 className="font-semibold text-gray-900 text-sm mb-2">Clausole Contrattuali Standard (SCC)</h4>
                <p className="text-xs text-gray-600">
                  Art. 46.2.c GDPR: Contratti approvati dalla Commissione UE (versione 2021) firmati con fornitori USA/extra-UE
                  per garantire protezioni equivalenti al GDPR
                </p>
              </div>

              <div className="border border-[#1F2937]/20 rounded-lg p-3">
                <h4 className="font-semibold text-gray-900 text-sm mb-2">Norme Vincolanti d'Impresa (BCR)</h4>
                <p className="text-xs text-gray-600">
                  Art. 47 GDPR: Utilizzate da gruppi multinazionali (es. AWS, Google) per trasferimenti intra-gruppo con garanzie privacy
                </p>
              </div>
            </div>

            <p className="text-gray-700 text-sm mb-4">
              <strong>Post-Schrems II:</strong> Dopo l'invalidazione del Privacy Shield (sentenza CGUE C-311/18), abbiamo verificato
              che i fornitori USA implementino garanzie supplementari tecniche e organizzative (crittografia, minimizzazione, segregazione dati UE)
              per mitigare rischi di accesso da parte di autorità governative USA.
            </p>

            {/* 6. PERIODO DI CONSERVAZIONE */}
            <h3 className="text-lg font-semibold text-gray-900 mb-3">6. Periodo di Conservazione dei Dati (Art. 5.1.e GDPR)</h3>
            <p className="text-gray-700 text-sm mb-4">
              Conserviamo i dati personali per il tempo strettamente necessario al perseguimento delle finalità indicate,
              in conformità al principio di minimizzazione e agli obblighi di legge:
            </p>

            <div className="overflow-x-auto mb-6">
              <table className="min-w-full border border-gray-200 rounded-lg">
                <thead className="bg-[#1F2937] text-white">
                  <tr>
                    <th className="px-4 py-2 text-left text-sm font-semibold border-b">Tipo di Dati</th>
                    <th className="px-4 py-2 text-left text-sm font-semibold border-b">Periodo di Conservazione</th>
                    <th className="px-4 py-2 text-left text-sm font-semibold border-b">Base Normativa</th>
                  </tr>
                </thead>
                <tbody className="text-sm">
                  <tr className="bg-white">
                    <td className="px-4 py-2 border-b text-gray-700">Dati Account attivo (registrazione, contatti)</td>
                    <td className="px-4 py-2 border-b text-gray-600">Durata del rapporto contrattuale + 30gg post-cancellazione (soft delete)</td>
                    <td className="px-4 py-2 border-b text-gray-600">Art. 6.1.b GDPR</td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="px-4 py-2 border-b text-gray-700">Dati fatturazione e contabilità</td>
                    <td className="px-4 py-2 border-b text-gray-600">10 anni dalla data di emissione fattura</td>
                    <td className="px-4 py-2 border-b text-gray-600">Art. 2220 c.c. / D.P.R. 600/1973 Art. 22</td>
                  </tr>
                  <tr className="bg-white">
                    <td className="px-4 py-2 border-b text-gray-700">Log di accesso e sicurezza</td>
                    <td className="px-4 py-2 border-b text-gray-600">6-12 mesi (max 24 mesi per indagini forensi)</td>
                    <td className="px-4 py-2 border-b text-gray-600">Provvedimento Garante Privacy 8 aprile 2010</td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="px-4 py-2 border-b text-gray-700">Dati marketing (consenso email, newsletter)</td>
                    <td className="px-4 py-2 border-b text-gray-600">Fino alla revoca del consenso (soft opt-out: 24 mesi inattività)</td>
                    <td className="px-4 py-2 border-b text-gray-600">Art. 6.1.a GDPR / D.Lgs. 196/2003 Art. 130</td>
                  </tr>
                  <tr className="bg-white">
                    <td className="px-4 py-2 border-b text-gray-700">Dati Business inseriti dal Cliente (CRM, progetti)</td>
                    <td className="px-4 py-2 border-b text-gray-600">Secondo istruzioni del Cliente Titolare (default: cancellazione 30gg post-terminazione)</td>
                    <td className="px-4 py-2 border-b text-gray-600">Art. 28 GDPR (Responsabile del Trattamento)</td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="px-4 py-2 border-b text-gray-700">Dati per contenziosi/difesa diritti</td>
                    <td className="px-4 py-2 border-b text-gray-600">Fino a definizione della controversia + termini prescrizione (10 anni per crediti)</td>
                    <td className="px-4 py-2 border-b text-gray-600">Art. 6.1.f GDPR / Art. 2946 c.c.</td>
                  </tr>
                  <tr className="bg-white">
                    <td className="px-4 py-2 text-gray-700">Cookie analytics e tracciamento</td>
                    <td className="px-4 py-2 text-gray-600">Max 13 mesi (cookie tecnici), 12 mesi (analytics con consenso)</td>
                    <td className="px-4 py-2 text-gray-600">Linee guida Garante 10 giugno 2021</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 mb-6">
              <p className="text-gray-700 text-sm">
                <strong>Cancellazione Automatica:</strong> Al termine dei periodi sopra indicati, i dati sono automaticamente
                cancellati dai sistemi di produzione tramite procedure di retention automatizzate. Backup archiviati per disaster recovery
                sono sovrascritti secondo policy rolling (30 giorni per backup giornalieri, 12 mesi per backup mensili).
              </p>
            </div>

            {/* 7. DIRITTI INTERESSATO GDPR - MOLTO DETTAGLIATO */}
            <h3 className="text-lg font-semibold text-gray-900 mb-3">7. Diritti dell'Interessato ai sensi del GDPR (Artt. 15-22)</h3>
            <p className="text-gray-700 text-sm mb-4">
              In qualità di interessato, hai il diritto di esercitare in qualsiasi momento i seguenti diritti scrivendo a <strong>digital@obly.it</strong>:
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              {/* Colonna 1 */}
              <div className="space-y-3">
                <div className="border border-[#1F2937]/20 rounded-lg p-3 bg-[#1F2937]/5">
                  <h4 className="font-semibold text-gray-900 text-sm mb-2">📖 Diritto di Accesso (Art. 15 GDPR)</h4>
                  <p className="text-gray-700 text-xs mb-2">
                    Ottenere <strong>conferma</strong> se stiamo trattando tuoi dati personali e, in caso affermativo, ricevere:
                  </p>
                  <ul className="text-gray-700 text-xs space-y-1 ml-4">
                    <li>• Copia dei dati in formato leggibile</li>
                    <li>• Finalità del trattamento</li>
                    <li>• Categorie di dati trattati</li>
                    <li>• Destinatari a cui sono comunicati</li>
                    <li>• Periodo di conservazione previsto</li>
                    <li>• Esistenza di decisioni automatizzate (profilazione)</li>
                  </ul>
                  <p className="text-gray-600 text-xs mt-2 italic">
                    Risposta entro <strong>30 giorni</strong>. Prima copia gratuita, copie ulteriori a costo amministrativo ragionevole.
                  </p>
                </div>

                <div className="border border-[#1F2937]/20 rounded-lg p-3 bg-[#1F2937]/5">
                  <h4 className="font-semibold text-gray-900 text-sm mb-2">✏️ Diritto di Rettifica (Art. 16 GDPR)</h4>
                  <p className="text-gray-700 text-xs">
                    Correggere dati personali inesatti o incompleti. Puoi aggiornare direttamente i tuoi dati di profilo
                    nelle impostazioni Account o richiedere assistenza per correzioni massive.
                  </p>
                </div>

                <div className="border border-[#1F2937]/20 rounded-lg p-3 bg-[#1F2937]/5">
                  <h4 className="font-semibold text-gray-900 text-sm mb-2">🗑️ Diritto alla Cancellazione - "Diritto all'Oblio" (Art. 17 GDPR)</h4>
                  <p className="text-gray-700 text-xs mb-2">
                    Richiedere la cancellazione immediata dei dati personali nei seguenti casi:
                  </p>
                  <ul className="text-gray-700 text-xs space-y-1 ml-4">
                    <li>• Non sono più necessari per le finalità</li>
                    <li>• Revoca consenso (se unica base giuridica)</li>
                    <li>• Opposizione al trattamento per marketing</li>
                    <li>• Trattati illecitamente</li>
                    <li>• Obbligo legale di cancellazione</li>
                  </ul>
                  <p className="text-gray-600 text-xs mt-2 italic">
                    <strong>Limitazioni:</strong> Non possiamo cancellare dati necessari per obblighi legali (fatture fiscali 10 anni),
                    per accertamento/difesa diritti in sede giudiziaria, o per legittimo interesse prevalente.
                  </p>
                </div>

                <div className="border border-[#1F2937]/20 rounded-lg p-3 bg-[#1F2937]/5">
                  <h4 className="font-semibold text-gray-900 text-sm mb-2">⏸️ Diritto di Limitazione (Art. 18 GDPR)</h4>
                  <p className="text-gray-700 text-xs mb-2">
                    Richiedere la limitazione (blocco temporaneo) del trattamento quando:
                  </p>
                  <ul className="text-gray-700 text-xs space-y-1 ml-4">
                    <li>• Contesti l'esattezza dei dati (per il tempo necessario alla verifica)</li>
                    <li>• Il trattamento è illecito ma non vuoi la cancellazione</li>
                    <li>• Ti servono i dati per difesa legale (anche se non più necessari a noi)</li>
                    <li>• Hai esercitato opposizione (in attesa di verifica interessi prevalenti)</li>
                  </ul>
                </div>
              </div>

              {/* Colonna 2 */}
              <div className="space-y-3">
                <div className="border border-[#1F2937]/20 rounded-lg p-3 bg-[#1F2937]/5">
                  <h4 className="font-semibold text-gray-900 text-sm mb-2">📦 Diritto alla Portabilità (Art. 20 GDPR)</h4>
                  <p className="text-gray-700 text-xs mb-2">
                    Ricevere i dati forniti in <strong>formato strutturato, di uso comune e leggibile da dispositivo automatico</strong>
                    (CSV, JSON, Excel) e trasmetterli a altro titolare, quando tecnicamente fattibile.
                  </p>
                  <p className="text-gray-700 text-xs mb-1">
                    <strong>Applicabile solo se:</strong>
                  </p>
                  <ul className="text-gray-700 text-xs space-y-1 ml-4">
                    <li>• Trattamento basato su consenso o contratto</li>
                    <li>• Trattamento effettuato con mezzi automatizzati</li>
                  </ul>
                  <p className="text-gray-600 text-xs mt-2 italic">
                    Flusia offre funzioni di esportazione massiva dati in formato standard direttamente dalla dashboard.
                  </p>
                </div>

                <div className="border border-[#1F2937]/20 rounded-lg p-3 bg-[#1F2937]/5">
                  <h4 className="font-semibold text-gray-900 text-sm mb-2">🚫 Diritto di Opposizione (Art. 21 GDPR)</h4>
                  <p className="text-gray-700 text-xs mb-2">
                    Opporti in qualsiasi momento al trattamento per:
                  </p>
                  <ul className="text-gray-700 text-xs space-y-1 ml-4 mb-2">
                    <li>• <strong>Marketing diretto:</strong> Opposizione assoluta, cessazione immediata (link unsubscribe nelle email)</li>
                    <li>• <strong>Legittimo interesse:</strong> Possiamo continuare solo se dimostriamo motivi legittimi cogenti prevalenti sui tuoi diritti</li>
                    <li>• <strong>Profilazione:</strong> Per decisioni basate su trattamento automatizzato (se applicabile)</li>
                  </ul>
                </div>

                <div className="border border-[#1F2937]/20 rounded-lg p-3 bg-[#1F2937]/5">
                  <h4 className="font-semibold text-gray-900 text-sm mb-2">↩️ Diritto di Revoca Consenso (Art. 7.3 GDPR)</h4>
                  <p className="text-gray-700 text-xs">
                    Ritirare il consenso in qualsiasi momento per trattamenti basati su consenso (es. newsletter marketing).
                    La revoca non pregiudica la liceità del trattamento effettuato prima della revoca.
                    La revoca è <strong>semplice quanto la raccolta</strong> del consenso (link unsubscribe, email, impostazioni profilo).
                  </p>
                </div>

                <div className="border border-[#1F2937]/20 rounded-lg p-3 bg-[#1F2937]/5">
                  <h4 className="font-semibold text-gray-900 text-sm mb-2">⚖️ Diritto di Reclamo all'Autorità (Art. 77 GDPR)</h4>
                  <p className="text-gray-700 text-xs mb-2">
                    Proporre reclamo al <strong>Garante per la protezione dei dati personali</strong> se ritieni che il trattamento
                    violi il GDPR:
                  </p>
                  <div className="bg-white rounded p-2 mt-2">
                    <p className="text-gray-700 text-xs">
                      <strong>Garante Privacy</strong><br />
                      Piazza Venezia 11, 00187 Roma<br />
                      Tel: +39 06.696771<br />
                      Email: garante@gpdp.it<br />
                      PEC: protocollo@pec.gpdp.it<br />
                      Web: <a href="https://www.garanteprivacy.it" className="text-[#1F2937] hover:underline" target="_blank" rel="noopener noreferrer">www.garanteprivacy.it</a>
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
              <h4 className="font-semibold text-gray-900 text-sm mb-2">📧 Come Esercitare i Tuoi Diritti</h4>
              <p className="text-gray-700 text-sm mb-2">
                Invia una richiesta via email a <strong>digital@obly.it</strong> con oggetto "Esercizio Diritti GDPR - [Nome Diritto]".
                Nella richiesta, specifica:
              </p>
              <ul className="text-gray-700 text-sm space-y-1 ml-4 mb-2">
                <li>• Nome, cognome, email registrata sull'Account</li>
                <li>• Diritto che intendi esercitare</li>
                <li>• Copia documento identità (per verifica identità e prevenzione accessi fraudolenti)</li>
              </ul>
              <p className="text-gray-700 text-sm">
                <strong>Tempistiche:</strong> Risponderemo entro <strong>30 giorni</strong> dalla richiesta. In casi complessi (es. richiesta
                accesso dati di grandi dimensioni), possiamo prorogare di ulteriori <strong>60 giorni</strong> informandoti entro il primo mese.
                La risposta sarà <strong>gratuita</strong>, salvo richieste manifestamente infondate o eccessive (costo ragionevole o rifiuto motivato).
              </p>
            </div>

            {/* Continua sezioni successive... */}

            {/* 8. MISURE DI SICUREZZA */}
            <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center space-x-2">
              <Lock className="w-5 h-5 text-[#1F2937]" />
              <span>8. Misure di Sicurezza Tecniche e Organizzative (Art. 32 GDPR)</span>
            </h3>
            <p className="text-gray-700 text-sm mb-4">
              Abbiamo implementato misure tecniche e organizzative adeguate per garantire un livello di sicurezza appropriato
              al rischio, tenendo conto dello stato dell'arte, dei costi di attuazione, della natura, oggetto, contesto e finalità
              del trattamento, nonché del rischio di varia probabilità e gravità per i diritti e le libertà delle persone fisiche:
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="border border-[#1F2937]/20 rounded-lg p-4 bg-[#1F2937]/5">
                <div className="flex items-center space-x-2 mb-2">
                  <Lock className="w-6 h-6 text-[#1F2937]" />
                  <h4 className="font-semibold text-gray-900 text-sm">Crittografia</h4>
                </div>
                <ul className="text-gray-700 text-xs space-y-1">
                  <li>• <strong>TLS 1.3</strong> per tutte le comunicazioni (HTTPS)</li>
                  <li>• Crittografia <strong>AES-256</strong> per dati a riposo (database)</li>
                  <li>• Crittografia end-to-end per backup</li>
                  <li>• Password hashate con <strong>bcrypt</strong> (salt individuale, costo 12)</li>
                  <li>• Chiavi gestite tramite HSM o KMS certificati</li>
                </ul>
              </div>

              <div className="border border-[#1F2937]/20 rounded-lg p-4 bg-[#1F2937]/5">
                <div className="flex items-center space-x-2 mb-2">
                  <Shield className="w-6 h-6 text-[#1F2937]" />
                  <h4 className="font-semibold text-gray-900 text-sm">Controllo Accessi</h4>
                </div>
                <ul className="text-gray-700 text-xs space-y-1">
                  <li>• Autenticazione <strong>multi-fattore (MFA/2FA)</strong> opzionale per utenti</li>
                  <li>• <strong>Role-Based Access Control (RBAC)</strong> granulare</li>
                  <li>• Principio <strong>"least privilege"</strong> e "need-to-know"</li>
                  <li>• Revisione trimestrale permessi accesso</li>
                  <li>• Sessioni con timeout automatico (inattività 30min)</li>
                </ul>
              </div>

              <div className="border border-[#1F2937]/20 rounded-lg p-4 bg-[#1F2937]/5">
                <div className="flex items-center space-x-2 mb-2">
                  <Database className="w-6 h-6 text-[#1F2937]" />
                  <h4 className="font-semibold text-gray-900 text-sm">Backup e Resilienza</h4>
                </div>
                <ul className="text-gray-700 text-xs space-y-1">
                  <li>• Backup <strong>automatici giornalieri</strong> crittografati</li>
                  <li>• Retention 30 giorni, backup mensili 12 mesi</li>
                  <li>• Replica geografica multi-region (EU)</li>
                  <li>• Disaster Recovery Plan (RPO 24h, RTO 4h)</li>
                  <li>• Test di restore trimestrali</li>
                </ul>
              </div>

              <div className="border border-[#1F2937]/20 rounded-lg p-4 bg-[#1F2937]/5">
                <div className="flex items-center space-x-2 mb-2">
                  <AlertCircle className="w-6 h-6 text-[#1F2937]" />
                  <h4 className="font-semibold text-gray-900 text-sm">Monitoraggio e Incident Response</h4>
                </div>
                <ul className="text-gray-700 text-xs space-y-1">
                  <li>• Logging centralizzato con SIEM</li>
                  <li>• Monitoraggio 24/7 anomalie e tentativi accesso</li>
                  <li>• Intrusion Detection/Prevention Systems (IDS/IPS)</li>
                  <li>• Security Incident Response Plan (SIRP)</li>
                  <li>• Data Breach Notification entro 72h a Garante (Art. 33 GDPR)</li>
                </ul>
              </div>

              <div className="border border-[#1F2937]/20 rounded-lg p-4 bg-[#1F2937]/5">
                <div className="flex items-center space-x-2 mb-2">
                  <Users className="w-6 h-6 text-[#1F2937]" />
                  <h4 className="font-semibold text-gray-900 text-sm">Misure Organizzative</h4>
                </div>
                <ul className="text-gray-700 text-xs space-y-1">
                  <li>• Formazione annuale GDPR per dipendenti</li>
                  <li>• Accordi riservatezza (NDA) con tutti collaboratori</li>
                  <li>• Clean desk policy e screen lock</li>
                  <li>• Registro Attività Trattamento (Art. 30 GDPR)</li>
                  <li>• Valutazione rischi privacy (DPIA) per trattamenti alto rischio</li>
                </ul>
              </div>

              <div className="border border-[#1F2937]/20 rounded-lg p-4 bg-[#1F2937]/5">
                <div className="flex items-center space-x-2 mb-2">
                  <Shield className="w-6 h-6 text-[#1F2937]" />
                  <h4 className="font-semibold text-gray-900 text-sm">Security Testing</h4>
                </div>
                <ul className="text-gray-700 text-xs space-y-1">
                  <li>• Penetration test annuali (white/grey box)</li>
                  <li>• Vulnerability scanning continuo</li>
                  <li>• Code review con focus sicurezza</li>
                  <li>• Dependency scanning per librerie terze</li>
                  <li>• Bug bounty program (valutazione futura)</li>
                </ul>
              </div>
            </div>

            {/* 9. DATA BREACH */}
            <h3 className="text-lg font-semibold text-gray-900 mb-3">9. Violazione Dati Personali (Data Breach) - Artt. 33-34 GDPR</h3>
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <p className="text-gray-700 text-sm mb-3">
                In caso di <strong>violazione dei dati personali</strong> (data breach: accesso non autorizzato, perdita, distruzione,
                modifica, divulgazione accidentale o illecita) che comporti un rischio per i diritti e le libertà degli interessati:
              </p>
              <ul className="text-gray-700 text-sm space-y-2 ml-4">
                <li>
                  <strong>Notifica al Garante Privacy (Art. 33 GDPR):</strong> Notificheremo la violazione all'Autorità Garante
                  entro <strong>72 ore</strong> dalla scoperta, salvo che la violazione non comporti un rischio per diritti/libertà.
                  Notifica include: natura violazione, categorie/numero interessati coinvolti, conseguenze probabili, misure adottate.
                </li>
                <li>
                  <strong>Notifica Diretta agli Interessati (Art. 34 GDPR):</strong> Se la violazione comporta un <strong>rischio elevato</strong>
                  per diritti/libertà (es. leak password non hashate, dati finanziari, dati sanitari), comunicheremo direttamente
                  agli interessati coinvolti senza ingiustificato ritardo, in linguaggio chiaro e semplice, con misure di mitigazione consigliate.
                </li>
                <li>
                  <strong>Registro Violazioni:</strong> Documentiamo tutte le violazioni in un registro interno, anche quelle
                  non notificate al Garante (perché non a rischio), come previsto dall'Art. 33.5 GDPR.
                </li>
              </ul>
            </div>

            {/* 10. COOKIE E TRACCIAMENTO */}
            <h3 className="text-lg font-semibold text-gray-900 mb-3">10. Cookie e Tecnologie di Tracciamento</h3>
            <p className="text-gray-700 text-sm mb-3">
              Il sito flusia.it e la piattaforma utilizzano <strong>cookie e tecnologie simili</strong> (local storage, pixel tag)
              conformemente alle "Linee guida cookie e altri strumenti di tracciamento" del Garante Privacy (10 giugno 2021):
            </p>

            <div className="space-y-3 mb-6">
              <div className="border border-gray-200 rounded-lg p-3">
                <h4 className="font-semibold text-gray-900 text-sm mb-2">🔧 Cookie Tecnici (Essenziali)</h4>
                <p className="text-gray-700 text-sm mb-2">
                  <strong>Non richiedono consenso</strong> perché strettamente necessari per il funzionamento del servizio:
                </p>
                <ul className="text-gray-700 text-sm space-y-1 ml-4">
                  <li>• Cookie di sessione autenticazione (session ID)</li>
                  <li>• Token CSRF per sicurezza form</li>
                  <li>• Preferenze lingua, tema UI, layout dashboard</li>
                  <li>• Cookie per load balancing</li>
                </ul>
                <p className="text-gray-600 text-xs mt-2 italic">Durata: Sessione o max 13 mesi</p>
              </div>

              <div className="border border-gray-200 rounded-lg p-3">
                <h4 className="font-semibold text-gray-900 text-sm mb-2">📊 Cookie Analytics</h4>
                <p className="text-gray-700 text-sm mb-2">
                  <strong>Richiedono consenso preventivo</strong> (salvo configurazione privacy-by-design con IP anonimizzato e dati aggregati):
                </p>
                <ul className="text-gray-700 text-sm space-y-1 ml-4">
                  <li>• <strong>Google Analytics 4</strong> con IP anonimizzato, advertising features disabilitati (se usato)</li>
                  <li>• Alternativamente: <strong>Plausible Analytics</strong> o <strong>Matomo</strong> (privacy-first, no cookie, GDPR compliant)</li>
                  <li>• Heatmaps e session recordings (es. Hotjar) solo con consenso esplicito</li>
                </ul>
                <p className="text-gray-600 text-xs mt-2 italic">Durata: Max 12 mesi con consenso</p>
              </div>

              <div className="border border-gray-200 rounded-lg p-3">
                <h4 className="font-semibold text-gray-900 text-sm mb-2">🎯 Cookie di Profilazione e Marketing</h4>
                <p className="text-gray-700 text-sm mb-2">
                  <strong>Attualmente NON utilizziamo</strong> cookie di profilazione per pubblicità comportamentale (es. Facebook Pixel, Google Ads retargeting).
                  Se in futuro implementati, richiederanno <strong>consenso esplicito</strong> tramite cookie banner conforme.
                </p>
              </div>
            </div>

            <p className="text-gray-700 text-sm mb-4">
              <strong>Gestione Cookie:</strong> Puoi gestire le preferenze cookie tramite il banner al primo accesso (opt-in),
              modificarle nelle impostazioni profilo, o configurare il tuo browser per bloccare/eliminare cookie (nota: bloccare cookie
              tecnici può compromettere funzionalità del servizio).
            </p>

            {/* 11. DECISIONI AUTOMATIZZATE E PROFILAZIONE */}
            <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center space-x-2">
              <Brain className="w-5 h-5 text-[#1F2937]" />
              <span>11. Decisioni Automatizzate e Profilazione (Art. 22 GDPR)</span>
            </h3>
            <p className="text-gray-700 text-sm mb-3">
              <strong>Utilizziamo profilazione automatizzata?</strong>
            </p>
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-4">
              <p className="text-gray-700 text-sm mb-2">
                <strong>Profilazione Limitata per Miglioramento UX (non decisioni automatiche):</strong>
              </p>
              <ul className="text-gray-700 text-sm space-y-1 ml-4 mb-3">
                <li>• <strong>Suggerimenti smart</strong> (es. completamento automatico nomi clienti, tag suggeriti) basati su pattern utilizzo aggregati</li>
                <li>• <strong>Classificazione automatica lead</strong> (es. scoring lead caldo/freddo) tramite algoritmi ML, ma sempre con supervisione umana finale</li>
                <li>• <strong>Raccomandazioni template</strong> (preventivi, email) basate su settore merceologico e utilizzo storico</li>
              </ul>
              <p className="text-gray-700 text-sm mb-2">
                <strong>Importante:</strong> Queste attività <strong>NON producono effetti giuridici</strong> automatici né incidono
                significativamente sugli interessati senza intervento umano. Non prendiamo decisioni <strong>esclusivamente automatizzate</strong>
                che impattano diritti (es. rigetto automatico richieste, modifiche prezzi individuali algoritmiche).
              </p>
              <p className="text-gray-700 text-sm">
                <strong>Diritto di Opposizione (Art. 21.2 GDPR):</strong> Hai comunque il diritto di opporti alla profilazione
                scrivendo a digital@obly.it. Puoi anche richiedere intervento umano, esprimere opinione, contestare decisioni.
              </p>
            </div>

            {/* 12. MARKETING DIRETTO - DETTAGLIATO */}
            <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center space-x-2">
              <Smartphone className="w-5 h-5 text-[#1F2937]" />
              <span>12. Marketing Diretto e Comunicazioni Commerciali (Art. 130 Codice Privacy)</span>
            </h3>

            <div className="space-y-3 mb-6">
              <div className="border border-[#1F2937]/20 rounded-lg p-3">
                <h4 className="font-semibold text-gray-900 text-sm mb-2">📧 Email Marketing</h4>
                <p className="text-gray-700 text-sm mb-2">
                  <strong>Per Clienti Esistenti (Soft Opt-In):</strong> Possiamo inviarti comunicazioni marketing su prodotti/servizi
                  analoghi a quelli già acquistati, senza necessità di consenso preventivo (Art. 130.4 Codice Privacy), ma con:
                </p>
                <ul className="text-gray-700 text-sm space-y-1 ml-4 mb-2">
                  <li>• Informativa chiara al momento della raccolta email</li>
                  <li>• Facile possibilità di opt-out in ogni comunicazione (link unsubscribe)</li>
                  <li>• Limite 24 mesi di inattività senza interazioni (dopo di che, re-opt-in richiesto)</li>
                </ul>
                <p className="text-gray-700 text-sm mb-2">
                  <strong>Per Prospect/Lead (Hard Opt-In):</strong> Per contatti che non sono mai stati clienti, richiediamo
                  <strong> consenso esplicito preventivo</strong> tramite checkbox non pre-spuntata e informativa completa.
                </p>
              </div>

              <div className="border border-[#1F2937]/20 rounded-lg p-3">
                <h4 className="font-semibold text-gray-900 text-sm mb-2">💬 WhatsApp Business Marketing</h4>
                <p className="text-gray-700 text-sm mb-2">
                  Per comunicazioni marketing via WhatsApp Business API richiediamo <strong>consenso opt-in esplicito</strong>,
                  in conformità a:
                </p>
                <ul className="text-gray-700 text-sm space-y-1 ml-4">
                  <li>• Termini WhatsApp Business API (Meta/Facebook)</li>
                  <li>• GDPR Art. 6.1.a (consenso) e Art. 130 Codice Privacy</li>
                  <li>• Direttiva ePrivacy (2002/58/CE)</li>
                </ul>
                <p className="text-gray-700 text-sm mt-2">
                  Ogni messaggio marketing include opzione "STOP" per opt-out immediato. Messaggi transazionali (es. conferme ordine,
                  notifiche spedizione) non richiedono consenso marketing.
                </p>
              </div>

              <div className="border border-[#1F2937]/20 rounded-lg p-3">
                <h4 className="font-semibold text-gray-900 text-sm mb-2">📱 SMS Marketing</h4>
                <p className="text-gray-700 text-sm">
                  Per SMS marketing richiediamo <strong>consenso esplicito preventivo</strong>. Al momento non inviamo SMS promozionali
                  massivi. SMS utilizzati solo per OTP (one-time password), conferme transazionali (esenti da consenso marketing).
                </p>
              </div>

              <div className="border border-[#1F2937]/20 rounded-lg p-3">
                <h4 className="font-semibold text-gray-900 text-sm mb-2">📞 Telemarketing</h4>
                <p className="text-gray-700 text-sm">
                  Al momento <strong>non effettuiamo chiamate di telemarketing</strong>. Chiamate telefoniche solo per supporto clienti
                  su richiesta esplicita del Cliente o per follow-up richieste demo (consenso implicito nella richiesta demo).
                </p>
              </div>
            </div>

            <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-6">
              <h4 className="font-semibold text-gray-900 text-sm mb-2">🔕 Come Revocare il Consenso Marketing (Opt-Out)</h4>
              <p className="text-gray-700 text-sm mb-2">
                Puoi <strong>opporti</strong> in qualsiasi momento alle comunicazioni marketing tramite:
              </p>
              <ul className="text-gray-700 text-sm space-y-1 ml-4">
                <li>• <strong>Link "Unsubscribe"</strong> presente in calce a ogni email marketing</li>
                <li>• <strong>Impostazioni Profilo</strong> sulla dashboard Flusia → Sezione "Preferenze Comunicazioni"</li>
                <li>• <strong>Risposta "STOP"</strong> ai messaggi WhatsApp marketing</li>
                <li>• <strong>Email a digital@obly.it</strong> con oggetto "Opt-Out Marketing"</li>
              </ul>
              <p className="text-gray-700 text-sm mt-2">
                L'opt-out sarà elaborato <strong>entro 48 ore</strong>. Continuerai a ricevere email transazionali essenziali
                (fatture, conferme, alert sicurezza), non marketing.
              </p>
            </div>

            {/* 13. DATI DI MINORI */}
            <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center space-x-2">
              <Baby className="w-5 h-5 text-[#1F2937]" />
              <span>13. Dati di Minori e Limiti di Età</span>
            </h3>
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6">
              <p className="text-gray-700 text-sm mb-3">
                Il Servizio Flusia è destinato <strong>esclusivamente a persone di età pari o superiore a 18 anni</strong>
                (o età maggiore richiesta dalla giurisdizione dell'utente) con capacità giuridica di stipulare contratti vincolanti.
              </p>
              <p className="text-gray-700 text-sm mb-3">
                <strong>Non raccogliamo consapevolmente dati personali di minori di 16 anni</strong> (età minima consenso digitale
                Art. 8 GDPR, Italia: 14 anni secondo Art. 2-quinquies Codice Privacy). Se utilizzi il Servizio per conto di un'azienda,
                dichiari che tutti gli utenti creati hanno almeno 16 anni (o 14 in Italia).
              </p>
              <p className="text-gray-700 text-sm mb-2">
                <strong>Procedura se Scopriamo Dati di Minori:</strong>
              </p>
              <ul className="text-gray-700 text-sm space-y-1 ml-4">
                <li>• Cancelliamo immediatamente i dati del minore</li>
                <li>• Sospend iamo l'Account coinvolto</li>
                <li>• Notifichiamo i genitori/tutori (se identificabili)</li>
                <li>• Documentiamo l'incidente nel registro violazioni</li>
              </ul>
              <p className="text-gray-700 text-sm mt-3">
                <strong>Se sei un genitore/tutore</strong> e scopri che tuo figlio minorenne ha fornito dati a Flusia senza
                consenso genitoriale, contattaci immediatamente a digital@obly.it per rimozione.
              </p>
            </div>

            {/* 14. FORNITORI TERZI DETTAGLIATI */}
            <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center space-x-2">
              <Building2 className="w-5 h-5 text-[#1F2937]" />
              <span>14. Elenco Dettagliato Fornitori Terzi (Sub-Responsabili)</span>
            </h3>
            <p className="text-gray-700 text-sm mb-4">
              Ci avvaliamo dei seguenti fornitori terzi qualificati (Sub-Responsabili del Trattamento ai sensi Art. 28.4 GDPR)
              con cui abbiamo stipulato Data Processing Agreements (DPA) conformi al GDPR:
            </p>

            <div className="overflow-x-auto mb-6">
              <table className="min-w-full border border-gray-200 rounded-lg text-sm">
                <thead className="bg-[#1F2937] text-white">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-semibold">Fornitore</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold">Servizio</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold">Localizzazione Dati</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold">Garanzie GDPR</th>
                  </tr>
                </thead>
                <tbody className="text-xs">
                  <tr className="bg-white">
                    <td className="px-3 py-2 border-b font-semibold text-gray-900">Supabase Inc.</td>
                    <td className="px-3 py-2 border-b text-gray-700">Database PostgreSQL, backend serverless, autenticazione</td>
                    <td className="px-3 py-2 border-b text-gray-600">Server UE (Frankfurt) + USA</td>
                    <td className="px-3 py-2 border-b text-gray-600">DPA firmato, SCC 2021, certificazioni SOC 2, ISO 27001</td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="px-3 py-2 border-b font-semibold text-gray-900">Stripe Inc.</td>
                    <td className="px-3 py-2 border-b text-gray-700">Elaborazione pagamenti, gestione carte credito</td>
                    <td className="px-3 py-2 border-b text-gray-600">USA (con data residency UE opzionale)</td>
                    <td className="px-3 py-2 border-b text-gray-600">DPA, SCC, PCI-DSS Level 1, SOC 1/2, GDPR compliant</td>
                  </tr>
                  <tr className="bg-white">
                    <td className="px-3 py-2 border-b font-semibold text-gray-900">SendGrid (Twilio)</td>
                    <td className="px-3 py-2 border-b text-gray-700">Invio email transazionali e marketing</td>
                    <td className="px-3 py-2 border-b text-gray-600">USA (server UE disponibili su richiesta)</td>
                    <td className="px-3 py-2 border-b text-gray-600">DPA, SCC, Privacy Shield successore, certificazioni ISO</td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="px-3 py-2 border-b font-semibold text-gray-900">Meta Platforms (WhatsApp Business)</td>
                    <td className="px-3 py-2 border-b text-gray-700">WhatsApp Business API per comunicazioni clienti</td>
                    <td className="px-3 py-2 border-b text-gray-600">Server Meta globali (inclusi USA)</td>
                    <td className="px-3 py-2 border-b text-gray-600">WhatsApp Business Terms, DPA Meta, SCC</td>
                  </tr>
                  <tr className="bg-white">
                    <td className="px-3 py-2 border-b font-semibold text-gray-900">Amazon Web Services (AWS)</td>
                    <td className="px-3 py-2 border-b text-gray-700">Cloud storage backup, CDN CloudFront</td>
                    <td className="px-3 py-2 border-b text-gray-600">EU-Central-1 (Francoforte)</td>
                    <td className="px-3 py-2 border-b text-gray-600">DPA AWS, SCC, certificazioni ISO 27001/27017/27018, SOC</td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="px-3 py-2 font-semibold text-gray-900">Google LLC (opzionale)</td>
                    <td className="px-3 py-2 text-gray-700">Analytics, Google Workspace (email aziendale)</td>
                    <td className="px-3 py-2 text-gray-600">Server globali Google (inclusi USA, UE)</td>
                    <td className="px-3 py-2 text-gray-600">DPA Google Cloud, SCC, certificazioni ISO, GDPR compliant</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <p className="text-gray-700 text-sm mb-6">
              <strong>Link alle Privacy Policy dei Fornitori:</strong>
            </p>
            <ul className="text-gray-700 text-sm space-y-1 ml-4 mb-6">
              <li>• Supabase: <a href="https://supabase.com/privacy" className="text-[#1F2937] hover:underline" target="_blank" rel="noopener noreferrer">supabase.com/privacy</a></li>
              <li>• Stripe: <a href="https://stripe.com/privacy" className="text-[#1F2937] hover:underline" target="_blank" rel="noopener noreferrer">stripe.com/privacy</a></li>
              <li>• SendGrid/Twilio: <a href="https://www.twilio.com/legal/privacy" className="text-[#1F2937] hover:underline" target="_blank" rel="noopener noreferrer">twilio.com/legal/privacy</a></li>
              <li>• WhatsApp Business: <a href="https://www.whatsapp.com/legal/business-policy" className="text-[#1F2937] hover:underline" target="_blank" rel="noopener noreferrer">whatsapp.com/legal/business-policy</a></li>
              <li>• AWS: <a href="https://aws.amazon.com/privacy" className="text-[#1F2937] hover:underline" target="_blank" rel="noopener noreferrer">aws.amazon.com/privacy</a></li>
            </ul>

            <h4 className="text-base font-semibold text-gray-900 mb-2">Servizi di terze parti attivati dal Cliente</h4>
            <p className="text-gray-700 text-sm mb-2">
              Il Cliente pu&ograve; collegare al CRM app esterne di intelligenza artificiale (ad esempio Claude di Anthropic o ChatGPT di OpenAI)
              tramite la pagina di autorizzazione di Flusia. Questi fornitori <strong>non sono Sub-Responsabili di Obly</strong>: il collegamento
              &egrave; una scelta del Cliente, che accetta i termini e la privacy policy del fornitore e risponde della liceit&agrave; della
              condivisione dei dati dei propri contatti (Termini di servizio, art. 13.2). I dati vengono trasmessi al fornitore solo dopo
              l'autorizzazione dell'utente, nei limiti del suo ruolo e della sua azienda, e il collegamento si revoca in qualsiasi momento da
              Impostazioni &gt; Informazioni Legali &gt; App collegate.
            </p>
            <ul className="text-gray-700 text-sm space-y-1 ml-4 mb-6">
              <li>• Anthropic (Claude): <a href="https://www.anthropic.com/privacy" className="text-[#1F2937] hover:underline" target="_blank" rel="noopener noreferrer">anthropic.com/privacy</a></li>
              <li>• OpenAI (ChatGPT): <a href="https://openai.com/policies/privacy-policy" className="text-[#1F2937] hover:underline" target="_blank" rel="noopener noreferrer">openai.com/policies/privacy-policy</a></li>
            </ul>

            {/* 15. MODIFICHE PRIVACY POLICY */}
            <h3 className="text-lg font-semibold text-gray-900 mb-3">15. Modifiche alla Privacy Policy</h3>
            <p className="text-gray-700 text-sm mb-3">
              Ci riserviamo il diritto di modificare o aggiornare questa Privacy Policy per riflettere:
            </p>
            <ul className="text-gray-700 text-sm space-y-1 ml-4 mb-3">
              <li>• Modifiche normative (nuove leggi, provvedimenti Garante)</li>
              <li>• Introduzione di nuove funzionalità o fornitori</li>
              <li>• Miglioramenti pratiche privacy e sicurezza</li>
              <li>• Feedback Garante Privacy o autorità competenti</li>
            </ul>
            <p className="text-gray-700 text-sm mb-4">
              Le modifiche <strong>sostanziali</strong> saranno comunicate con <strong>almeno 15 giorni di preavviso</strong> tramite:
              (a) email all'indirizzo registrato; (b) notifica in-app visibile al primo accesso; (c) aggiornamento data "Ultimo aggiornamento"
              in testa al documento. Versioni storiche disponibili su richiesta a digital@obly.it. L'uso continuato del Servizio dopo
              l'entrata in vigore delle modifiche costituisce accettazione della Privacy Policy aggiornata.
            </p>

            {/* 16. CONTATTI PRIVACY */}
            <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center space-x-2">
              <Mail className="w-5 h-5 text-[#1F2937]" />
              <span>16. Contatti per Questioni Privacy e DPO</span>
            </h3>
            <div className="bg-[#1F2937]/10 border border-[#1F2937]/20 rounded-lg p-4 mb-6">
              <p className="text-gray-700 text-sm mb-3">
                Per qualsiasi domanda, richiesta o dubbio riguardante questa Privacy Policy, il trattamento dei tuoi dati personali,
                o per esercitare i tuoi diritti GDPR, puoi contattarci:
              </p>
              <div className="bg-white border border-gray-200 rounded p-3">
                <p className="text-gray-700 text-sm">
                  <strong>Obly Srls - Titolare del Trattamento</strong><br />
                  Piazza Martiri della Libertà 2, 15033 Casale Monferrato (AL), Italia<br />
                  Partita IVA: 02730180060<br />
                  <br />
                  <strong>Email Privacy:</strong> <a href="mailto:digital@obly.it" className="text-[#1F2937] hover:underline font-semibold">digital@obly.it</a><br />
                  <strong>Oggetto Email:</strong> "Privacy / GDPR - [Tipo Richiesta]"<br />
                  <br />
                  <strong>Data Protection Officer (DPO):</strong> Non nominato (non obbligatorio ex Art. 37 GDPR)<br />
                  <strong>Responsabile Privacy Interno:</strong> Contattare via email sopra indicata
                </p>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <h4 className="font-semibold text-gray-900 text-sm mb-2">📋 Registro delle Attività di Trattamento (Art. 30 GDPR)</h4>
              <p className="text-gray-700 text-sm">
                Manteniamo un <strong>Registro delle Attività di Trattamento</strong> interno conforme all'Art. 30 GDPR, contenente:
                finalità, categorie dati/interessati, destinatari, trasferimenti extra-UE, termini conservazione, misure sicurezza.
                Il Registro è disponibile su richiesta del Garante Privacy o in caso di ispezione. Gli interessati possono richiedere
                estratti pertinenti ai propri dati scrivendo a digital@obly.it.
              </p>
            </div>

            {/* Box finale */}
            <div className="bg-[#1F2937]/5 border border-[#1F2937]/20 rounded-lg p-4">
              <p className="text-gray-700 text-sm text-center">
                <strong>Prendendo visione di questa Privacy Policy, dichiari di essere stato informato in modo chiaro e completo
                sul trattamento dei tuoi dati personali ai sensi degli Artt. 13-14 GDPR.</strong>
                <br /><br />
                <span className="text-xs text-gray-600">
                  Ultimo aggiornamento: {new Date().toLocaleDateString('it-IT')}<br />
                  Versione: 2.0 (GDPR-compliant dettagliata)
                </span>
              </p>
            </div>

          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end p-4 sm:p-6 border-t border-gray-200 bg-gray-50 shrink-0 rounded-b-xl">
          <button
            onClick={onClose}
            className="px-6 py-2.5 bg-[#1F2937] hover:bg-[#1F2937]/90 text-white font-semibold rounded-lg transition-colors shadow-sm"
          >
            Ho Letto e Compreso
          </button>
        </div>
      </div>
    </div>
  );
}
